/**
 * Receive Lot — Owner UI.
 *
 * Route: /food-pulse/owner/inventory/receive?business_id=…
 * Flow : Barcode/GTIN → parse → master lookup (auto-stub if unknown)
 *        → fill lot form (qty, unit, cost, expiry) → save.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ScanLine, Package, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Plus } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";

export default function OwnerInventoryReceive() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const businessId = params.get("business_id") || "";
  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState(null);
  const [product, setProduct] = useState(null);
  const [needsConfirm, setNeedsConfirm] = useState(false);   // NEW: gate for confirm-new-product
  const [form, setForm] = useState({
    product_name: "",
    qty: 1,
    unit: "each",
    cost_per_unit: "",
    lot_no: "",
    expiry: "",
    condition_score: 100,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const scan = async () => {
    setError(""); setNeedsConfirm(false); setProduct(null);
    if (!raw.trim() || authLoading) return;
    try {
      const r = await http.post("/inventory/barcode/parse", { raw: raw.trim() });
      setParsed(r.data.parsed);
      if (r.data.parsed.gtin) {
        // Read-only search first — do NOT auto-create.
        const s = await http.post("/inventory/master/search", { gtin: r.data.parsed.gtin });
        if ((s.data.items || []).length > 0) {
          const p = s.data.items[0];
          setProduct(p);
          setForm((f) => ({ ...f, product_name: p.name || "", lot_no: r.data.parsed.lot || "", expiry: r.data.parsed.expiry || "" }));
        } else {
          // Unknown GTIN — require an explicit "confirm" tap.
          setNeedsConfirm(true);
          setForm((f) => ({ ...f, product_name: "", lot_no: r.data.parsed.lot || "", expiry: r.data.parsed.expiry || "" }));
        }
      }
    } catch (e) { setError(e.response?.data?.detail || "Parse failed"); }
  };

  const confirmCreateStub = async () => {
    if (!parsed?.gtin) return;
    setError("");
    try {
      const r = await http.post("/inventory/master/lookup", {
        gtin: parsed.gtin,
        name: form.product_name || `Unknown product (${parsed.gtin})`,
      });
      setProduct(r.data.product);
      setNeedsConfirm(false);
      setForm((f) => ({ ...f, product_name: r.data.product.name || f.product_name }));
    } catch (e) { setError(e.response?.data?.detail || "Create failed"); }
  };

  const suggestInstead = () => {
    // Deep-link into the full user product-suggestion form.
    const qs = new URLSearchParams();
    if (parsed?.gtin) qs.set("gtin", parsed.gtin);
    if (form.product_name) qs.set("name", form.product_name);
    nav(`/food-pulse/suggest-product?${qs.toString()}`);
  };

  const save = async () => {
    setBusy(true); setError("");
    try {
      const body = {
        business_id: businessId,
        product_id: product?.id,
        gtin: parsed?.gtin,
        product_name: form.product_name,
        lot_no: form.lot_no || undefined,
        qty: Number(form.qty),
        unit: form.unit,
        cost_per_unit: form.cost_per_unit ? Number(form.cost_per_unit) : undefined,
        expiry: form.expiry || undefined,
        condition_score: Number(form.condition_score) || 100,
        notes: form.notes || undefined,
      };
      const r = await http.post("/inventory/lots", body);
      nav(`/food-pulse/owner/inventory/lots/${r.data.id}`);
    } catch (e) { setError(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!user) return <div className="p-10 text-center">Sign in required.</div>;
  if (!businessId) return <div className="p-10 text-center">Missing business_id in URL.</div>;

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="owner-inventory-receive">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to={`/food-pulse/owner/inventory?business_id=${businessId}`} className="inline-flex items-center gap-1 text-sm font-semibold mb-4" style={{ color: GREEN }} data-testid="back-btn">
          <ArrowLeft size={14} /> Back to inventory
        </Link>
        <h1 className="text-2xl font-black mb-2" style={{ color: GREEN }}>Receive a lot</h1>
        <p className="text-sm text-gray-600 mb-6">Scan a GS1 barcode or paste the raw code — we'll find the product and pre-fill your lot details.</p>

        <div className="rounded-2xl bg-white border p-5 mb-4" style={{ borderColor: "#E9D8B4" }}>
          <label className="text-xs font-semibold text-gray-600">Barcode / GTIN / GS1</label>
          <div className="flex gap-2 mt-1">
            <input value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="(01)00614141999996(10)ABC1(17)261231" className="flex-1 border rounded-full px-4 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="barcode-input" />
            <button onClick={scan} disabled={authLoading || !raw.trim()} className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1 disabled:opacity-50" style={{ background: GREEN, color: CREAM }} data-testid="scan-btn">
              <ScanLine size={14} /> Scan
            </button>
          </div>
          {parsed && (
            <div className="mt-3 text-xs text-gray-700" data-testid="parsed-summary">
              GTIN: <b>{parsed.gtin || "—"}</b> · Lot: <b>{parsed.lot || "—"}</b> · Expiry: <b>{parsed.expiry || "—"}</b>
            </div>
          )}
          {product && (
            <div className="mt-3 rounded-xl border p-3 text-sm flex items-center gap-3" style={{ borderColor: "#E9D8B4" }} data-testid="matched-product">
              <Package size={20} style={{ color: GREEN }} />
              <div>
                <div className="font-semibold" style={{ color: GREEN }}>{product.name}</div>
                <div className="text-xs text-gray-600">Status: {product.status}{product.status === "pending_review" ? " (auto-stubbed, admin will review)" : ""}</div>
              </div>
            </div>
          )}
          {needsConfirm && (
            <div className="mt-3 rounded-xl border-2 p-3 text-sm" style={{ borderColor: "#F59E0B", background: "#FEF3C7" }} data-testid="confirm-new-product">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={18} className="mt-0.5" style={{ color: "#B45309" }} />
                <div>
                  <div className="font-bold" style={{ color: "#B45309" }}>Unknown product — please confirm</div>
                  <div className="text-xs text-gray-700 mt-0.5">
                    GTIN <b>{parsed?.gtin}</b> is not in FoodPulse yet. Please type the product name below and confirm to add it — or use <b>Suggest a product</b> to enter richer details (photos, origin, etc.).
                  </div>
                </div>
              </div>
              <input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Product name (e.g. Organic Bananas)" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#F59E0B" }} data-testid="confirm-name-input" />
              <div className="flex gap-2 mt-2">
                <button onClick={confirmCreateStub} disabled={!form.product_name.trim()} className="rounded-full px-3 py-2 text-xs font-semibold disabled:opacity-50" style={{ background: GREEN, color: CREAM }} data-testid="confirm-create-btn">
                  <Plus size={12} className="inline mr-1" /> Yes, add "{form.product_name || '…'}" for admin review
                </button>
                <button onClick={suggestInstead} className="rounded-full border px-3 py-2 text-xs font-semibold" style={{ borderColor: GREEN, color: GREEN }} data-testid="suggest-instead-btn">Suggest a product instead</button>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white border p-5 grid gap-3" style={{ borderColor: "#E9D8B4" }} data-testid="lot-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600">Product name</label>
              <input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="product-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Quantity</label>
              <input type="number" step="0.01" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="qty-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Unit</label>
              <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="unit-select">
                {["each", "lb", "kg", "oz", "box", "case", "bushel"].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Cost / unit</label>
              <input type="number" step="0.01" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="cost-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Lot #</label>
              <input value={form.lot_no} onChange={(e) => setForm({ ...form, lot_no: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="lot-no-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Expiry</label>
              <input type="date" value={form.expiry?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, expiry: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="expiry-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Condition (0-100)</label>
              <input type="number" min="0" max="100" value={form.condition_score} onChange={(e) => setForm({ ...form, condition_score: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="condition-input" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600">Notes</label>
              <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="notes-input" />
            </div>
          </div>
          {error && <div className="text-sm text-red-700" data-testid="error-msg">{error}</div>}
          <button disabled={busy || !form.product_name || !form.qty || needsConfirm} onClick={save} className="rounded-full px-4 py-3 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: GREEN, color: CREAM }} data-testid="save-lot-btn">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Save lot
          </button>
        </div>
      </div>
    </main>
  );
}
